export const SmallSmallPolicy = [
  {
    section: '1. Purpose and Scope',
    purpose: [
      'Provide clear guidance on how returns, replacements, and refunds are managed on SmallSmall platforms.',
      'Define the responsibilities of buyers, vendors, and delivery companies.',
      'Ensure transparency in handling complaints and disputes.',
      'Establish timelines for notification, inspection, approval, and refund issuance.',
    ],
    appliesTo: [
      'Products purchased (electronics, home goods, fashion, etc.).',
      'Installment-based transactions such as Buy Now, Pay Later or Own Now, Pay Later.',
      'Service-related transactions such as property rentals.',
    ],
  },
  {
    section: '2. Definitions',
    terms: [
      {
        term: 'Buyer',
        definition:
          'Any individual or entity purchasing goods or services via a SmallSmall platform.',
      },
      {
        term: 'Vendor',
        definition:
          'The seller or supplier listing products on SmallSmall platforms.',
      },
      {
        term: 'Delivery Partner',
        definition:
          'The Logistics Company or courier responsible for transporting products to buyers.',
      },
      {
        term: 'Defective Item',
        definition:
          'A product that does not meet quality standards, is damaged, or is unfit for use at the point of delivery.',
      },
      {
        term: 'Return Window',
        definition:
          'The maximum timeframe (24 hours from delivery) within which a buyer may report an issue.',
      },
      {
        term: 'Refund Timeline',
        definition:
          'The time (7–14 business days) within which refunds will be processed after approval.',
      },
      {
        term: 'Non-returnable Items',
        definition:
          'Categories of goods excluded from the return process for health, safety, or regulatory reasons.',
      },
    ],
  },
  {
    section: '3. Eligibility for Returns',
    conditions: [
      'The product is defective or damaged at the time of delivery.',
      'The buyer receives a wrong item that is materially different from what was ordered.',
    ],
    requirements: [
      'The product must be returned in its original packaging, with all manuals, accessories, and labels intact.',
      'The product must be unused and free from any additional damage after delivery.',
    ],
    nonReturnable: [
      'Perishable or consumable goods (e.g., food, beverages).',
      'Hygiene-sensitive products (e.g., cosmetics, undergarments, medical items).',
      "Items marked as 'Final Sale' or 'Non-Returnable' on the product listing page.",
    ],
  },
  {
    section: '4. Notification Timeline',
    details: [
      'Buyers must notify SmallSmall Customer Support of any defect, damage, or incorrect item within 24 hours of receiving the product.',
      'The notification must include evidence such as photos or videos clearly showing the issue.',
      'Requests submitted after the 24-hour period will be considered invalid, and the buyer assumes full responsibility for the item.',
    ],
  },
  {
    section: '5. Responsibility for Defective or Damaged Items',
    vendorLiability:
      'Vendors are responsible for items that were already defective before shipment. Vendors must cover the cost of return and provide either a replacement or refund.',
    deliveryLiability:
      'If an item is damaged during shipping, the delivery company is responsible for covering the associated loss.',
    buyerResponsibility: [
      'If defects are reported after the 24-hour window, the buyer bears responsibility, and no refund will be issued.',
      'Where items are misused, mishandled, or altered post-delivery, buyers will also bear full responsibility.',
    ],
  },
  {
    section: '6. Return Process',
    steps: [
      {
        step: '1. Initiating a Request',
        details: [
          'Contact SmallSmall Customer Support within 24 hours of delivery.',
          'Provide order details and evidence of the defect.',
        ],
      },
      {
        step: '2. Review and Approval',
        details: [
          'Customer Support will review the request.',
          'If approved, return instructions and the approved logistics channel will be communicated.',
        ],
      },
      {
        step: '3. Return Delivery',
        details: [
          'Buyers are responsible for delivery costs unless liability rests with the vendor or courier.',
          'Returned items must be securely packaged to prevent further damage.',
        ],
      },
      {
        step: '4. Proof of Purchase',
        details: [
          'All returns must include proof of purchase such as receipts or digital order confirmations.',
        ],
      },
    ],
  },
  {
    section: '7. Refunds and Replacements',
    rules: [
      'Refunds or replacements are only processed after inspection by SmallSmall Quality Control.',
      'Refunds are processed within 7–14 business days using the original payment method.',
      'Approved replacement items are shipped promptly, subject to availability.',
    ],
    declinedRefunds: [
      'The request is outside the return window.',
      'The product shows evidence of misuse or tampering.',
      'The product is a non-returnable category item.',
    ],
  },
  {
    section: '8. Exceptions',
    exceptions: [
      'Items bought during clearance or flash sales may not be returnable unless otherwise specified.',
      'Refunds for Buy Now, Pay Later or Own Now, Pay Later transactions will be first applied toward outstanding balances. Remaining balances will be credited back to the buyer.',
      'Refunds related to rental payments (RentSmallSmall) or service fees will be governed by the respective service agreement.',
    ],
  },
  {
    section: '9. Platform-Specific Rules',
    platforms: [
      { name: 'SmallSmall', rule: 'Standard product return rules apply.' },
      {
        name: 'RentSmallSmall',
        rule: 'Refunds for rentals may only be given in cases of fraud, double payment, or service non-delivery. Refunds are not granted for change of preference after lease activation.',
      },
      {
        name: 'Other Platforms',
        rule: 'Terms may be tailored based on the nature of goods or services.',
      },
    ],
  },
  {
    section: '10. Legal Provisions',
    provisions: [
      'SmallSmall is not liable for losses beyond the cost of the product itself.',
      'SmallSmall reserves the right to amend this policy at any time, with updates posted on our website.',
      'This policy shall be governed by the laws of the Federal Republic of Nigeria.',
    ],
  },
  {
    section: '11. Frequently Asked Questions (FAQ)',
    faqs: [
      {
        q: 'Can I return an item if I simply change my mind?',
        a: 'No. Returns are only accepted for defective, damaged, or incorrect items.',
      },
      {
        q: 'Who pays for return shipping?',
        a: 'The buyer, unless the issue is caused by the vendor or delivery company.',
      },
      {
        q: 'How long do refunds take?',
        a: 'Between 7–14 business days after inspection and approval.',
      },
      {
        q: 'Can I get a refund for a rental property?',
        a: 'Refunds for RentSmallSmall depend on the rental agreement terms.',
      },
      {
        q: 'What happens if the product I ordered is out of stock for replacement?',
        a: 'A full refund will be processed instead.',
      },
    ],
  },
  {
    section: '12. Contact Information',
    contact: {
      email: 'support@smallsmall.com',
      note: 'For all return and refund-related inquiries, please contact via email.',
    },
  },
];
